(() => {
  const TRACK_SELECTOR = '[data-blaze-reviews-track]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const AUTOPLAY_INTERVAL = 4500;
  const INTERACTION_PAUSE = 6500;

  const getClosestCardIndex = (track, cards) => {
    const trackRect = track.getBoundingClientRect();
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const trackEdge = isRtl ? trackRect.right : trackRect.left;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const cardEdge = isRtl ? cardRect.right : cardRect.left;
      const distance = Math.abs(cardEdge - trackEdge);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const configureTrack = (track) => {
    if (track.dataset.blazeReviewsAutoplay === 'true') return;

    const cards = [...track.querySelectorAll('.blaze-review')];
    if (cards.length < 2) return;

    track.dataset.blazeReviewsAutoplay = 'true';

    let intervalId;
    let resumeTimeoutId;
    let scrollTimeoutId;
    let autoScrollTimeoutId;
    let isInView = true;
    let isInteracting = false;
    let isAutoScrolling = false;
    let activeIndex = getClosestCardIndex(track, cards);

    const canAutoplay = () =>
      MOBILE_QUERY.matches &&
      !REDUCED_MOTION_QUERY.matches &&
      isInView &&
      !isInteracting &&
      !document.hidden;

    const stopAutoplay = () => {
      window.clearInterval(intervalId);
      intervalId = undefined;
    };

    const showNextCard = () => {
      activeIndex = (activeIndex + 1) % cards.length;
      const nextCard = cards[activeIndex];
      isAutoScrolling = true;

      nextCard.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });

      window.clearTimeout(autoScrollTimeoutId);
      autoScrollTimeoutId = window.setTimeout(() => {
        isAutoScrolling = false;
        activeIndex = getClosestCardIndex(track, cards);
      }, 800);
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!canAutoplay()) return;

      intervalId = window.setInterval(showNextCard, AUTOPLAY_INTERVAL);
    };

    const pauseForInteraction = () => {
      isInteracting = true;
      stopAutoplay();
      window.clearTimeout(resumeTimeoutId);
    };

    const resumeAfterInteraction = () => {
      window.clearTimeout(resumeTimeoutId);
      resumeTimeoutId = window.setTimeout(() => {
        isInteracting = false;
        startAutoplay();
      }, INTERACTION_PAUSE);
    };

    track.addEventListener('pointerdown', pauseForInteraction, { passive: true });
    track.addEventListener('pointerup', resumeAfterInteraction, { passive: true });
    track.addEventListener('pointercancel', resumeAfterInteraction, { passive: true });
    track.addEventListener('mouseenter', pauseForInteraction);
    track.addEventListener('mouseleave', resumeAfterInteraction);
    track.addEventListener('focusin', pauseForInteraction);
    track.addEventListener('focusout', resumeAfterInteraction);
    track.addEventListener(
      'scroll',
      () => {
        if (isAutoScrolling) return;

        window.clearTimeout(scrollTimeoutId);
        scrollTimeoutId = window.setTimeout(() => {
          activeIndex = getClosestCardIndex(track, cards);
        }, 160);
      },
      { passive: true }
    );

    const handlePreferenceChange = () => {
      if (REDUCED_MOTION_QUERY.matches) {
        isInteracting = false;
        window.clearTimeout(resumeTimeoutId);
      }
      startAutoplay();
    };

    MOBILE_QUERY.addEventListener('change', handlePreferenceChange);
    REDUCED_MOTION_QUERY.addEventListener('change', handlePreferenceChange);
    document.addEventListener('visibilitychange', startAutoplay);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          isInView = entry.isIntersecting;
          startAutoplay();
        },
        { threshold: 0.35 }
      );

      observer.observe(track);
    }

    startAutoplay();
  };

  const initialize = (scope = document) => {
    if (scope instanceof Element && scope.matches(TRACK_SELECTOR)) {
      configureTrack(scope);
    }

    scope.querySelectorAll(TRACK_SELECTOR).forEach(configureTrack);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
